/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useState, useEffect, useContext } from 'react';
import { useQuery } from 'react-query';
import { isNil, sumBy } from 'lodash';
import { SwapOutlined } from '@ant-design/icons';
import {
  Col,
  Form,
  Checkbox,
  notification,
  Popover,
  Row,
  FormInstance,
  Tooltip,
} from 'antd';
import DF from 'DisplayFramework';
import AntButton from 'components/Button';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { CalculatorTwoTone, AccountBookTwoTone } from '@ant-design/icons';
import {
  CiLateCharge,
  getLateChargeByTpid,
  calculateLateChargeByTpid,
  genLateChargeInvoiceBytpid,
  updateInvoiceLateCalculationStatus,
} from 'services/clientInvoice';
import Title from 'components/Title';
import { toCurrency } from 'utils/utils';
import Value from 'components/Value';
import FormInput from 'components/FormInput';
import Button from 'components/Button';
import GetLabel from 'DisplayFramework/components/GetLabel';
import CIDetail from '../CIDetail';
import ModalControllerNV from 'components/ModalControllerNV';
import InputCurrency from 'components/InputCurrency';
import { AuthContext } from 'components/Auth/Auth';
import ProposalModal from 'pages/ProposalV3/ProposalModal';

type Values = {
  LateCharge: CiLateCharge[];
};
export type LateChargeProps = {
  tpid?: number;
  showAsModal?: boolean;
  formG: FormInstance;
};
const LateCharge = ({ tpid, showAsModal, formG }: LateChargeProps) => {
  const { isInspect } = useContext(AuthContext);
  const [form] = Form.useForm();

  const [visible, setVisible] = useState<any>(false);

  const [state, setState] = useState<any>({
    isLoading: false,
    dataCalc: [],
    totalLateCharge: 0,
    totalInvoiced: 0,
    totalDueAmount: 0,
    totalSelected: 0,
    listCalid: [],
    lastCalcDate: '',
  });
  const updateState = useCallback(
    (values: any) => {
      setState((prev: any) => ({ ...prev, ...values }));
    },
    [setState]
  );
  const { data, refetch, isFetching } = useQuery(
    ['getLateChargeByTpid', tpid],
    () =>
      getLateChargeByTpid({ tpid: tpid ?? 0 }).then((res) => {
        return res.list || [];
      }),
    { staleTime: 60 * 10000 }
  );

  useEffect(() => {
    if (data) {
      updateState({
        dataCalc: data,
        totalLateCharge: sumBy(data, 'calculatedamount'),
        totalDueAmount: sumBy(data, 'cidueamount'),
        totalInvoiced: sumBy(data, 'invoicedamount'),
        lastCalcDate: data[0]?.caldate,
      });
    } else {
      updateState({
        dataCalc: [data],
        totalLateCharge: 0,
        totalDueAmount: 0,
        totalInvoiced: 0,
        lastCalcDate: '',
      });
      form.setFieldsValue({totalSelected:state.totalSelected})
      refetch();
    }
  }, [data, refetch, updateState]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateStatus = (calid: number, flagblockinvoicing: boolean) => {
    updateInvoiceLateCalculationStatus({
      tpid: Number(tpid),
      calid: calid,
      flagblockinvoicing: flagblockinvoicing,
      blockingreason: 'NONE',
    }).then((response) => {
      if (response.code === '200') {
        notification.success({
          message: response.message,
        });
        refetch();
      }
    });
  };
  const columns = useMemo<DataGridColDef<CiLateCharge>[]>(
    () => [
      {
        dataIndex: 'cireference',
        width: '150px',
        render: (value, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{record.cireference}</a>
            )}
            modal={({ setVisible, visible }) => (
              <CIDetail
                ciid={record.ciid}
                onCancel={() => setVisible(false)}
                showAsModal
                visible={visible}
              />
            )}
          />
        ),
      },
      {
        dataIndex: 'citype',

        width: '120px',
      },
      {
        dataIndex: 'lctype',
        render: (value) =>
          value === 'CALCUL' ? (
            <Tooltip title="Calculated" color="red">
              <CalculatorTwoTone twoToneColor="red" />
            </Tooltip>
          ) : (
            <Tooltip title="Invoiced" color="green">
              <AccountBookTwoTone twoToneColor="green" />
            </Tooltip>
          ),
        width: '120px',
      },
      {
        dataIndex: 'ctreference',
        width: '100px',
      render: (value, record) => {
        return (
          <ModalControllerNV
            modal={({ setVisible, visible }) => (
              <ProposalModal
                ctrid={record.ctrid}
                visible={visible}
                onCancel={() => setVisible(false)}
                onSuccess={() => setVisible(false)}
                title=""
                iscreate={false}
                phase='PROD'
              />
            )}
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{record.ctreference}</a>
            )}
          />
        );
      }},

     
      {
        dataIndex: 'cipaymentmode',
        width: '100px',
      },
      {
        dataIndex: 'cidocdate',
        width: '100px',
      },
      {
        dataIndex: 'cigrosstotal',
        width: '100px',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),
      },
      {
        dataIndex: 'cidueamount',
        width: '100px',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),
      },
      {
        dataIndex: 'cipaymentdate',
        width: '100px',
      },
      {
        dataIndex: 'caldate',
        width: '100px',
        render: (value, record) => (
          <Popover
            title={<GetLabel label='Calculation detail'/>}
            style={{ width: 600, backgroundColor: 'green', height: 400 }}
            placement="right"
            content={
              <div style={{ width: 600, height: 300 }}>
                <Row gutter={[16, 16]}>
                  <Col span={4}> <GetLabel label='Basis'/> </Col>
                  <Col span={4}> <GetLabel label='Startdate'/> </Col>
                  <Col span={4}> <GetLabel label='Enddate'/></Col>
                  <Col span={4}> <GetLabel label='Nbdays'/> </Col>
                  <Col span={4}> <GetLabel label='Rate'/> </Col>
                  <Col span={4}> <GetLabel label='Amount'/> </Col>
                </Row>

                {record.ciLateChargeDetailList?.map((line, idx) => (
                  <div key={idx}>
                    <Row gutter={[16, 16]}>
                      <Col span={24}></Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col span={4}>
                        {' '}
                        {toCurrency(
                          line.libasis,
                          formG.getFieldValue('currcode')
                        )}{' '}
                      </Col>
                      <Col span={4}> {line.listartdate} </Col>
                      <Col span={4}> {line.lienddate}</Col>
                      <Col span={4}> {line.linbdays}</Col>
                      <Col span={4}> {line.liinterestrate} </Col>
                      <Col span={4}>
                        {' '}
                        {toCurrency(
                          line.licalculatedamount,
                          formG.getFieldValue('currcode')
                        )}{' '}
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            }
          >
            <a>{value}</a>
          </Popover>
        ),
      },
      {
        dataIndex: 'calculatedamount',
        width: '100px',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),
      },
      {
        dataIndex: 'flagblockinvoicing',
        width: '100px',
        render: (value) => <Checkbox checked={value} />,
      },

      {
        dataIndex: 'invoicedamount',
        width: '100px',
        render: (val, record) => toCurrency(val, record.currcode ?? localStorage.getItem('currcode') ?? ''),
      },
      {
        dataIndex: 'action',
        width: '150px',
        fixed: 'right',
        render: (text, record) => (
          <Row>
            <AntButton
              icon={<SwapOutlined twoToneColor="#52c41a" />}
              comcode={record.flagblockinvoicing===true?"unblokinginvoicing":"blokinginvoicing"}
              onClick={(e) => 
               !isInspect&& updateStatus(record.calid, !record.flagblockinvoicing)
              }
            />
          </Row>
        ),
      },
    ],


    [ isInspect, updateStatus]
  );

  const calculate = () => {
    state.isLoading = true;
    setState({ ...state, isLoading: true });
    calculateLateChargeByTpid({ tpid: tpid ?? 0 }).then((res) => {
      state.dataCalc = res.list || [];
      state.isLoading = false;
      refetch();
      setState({ ...state, isLoading: false });
    });
  };
  const invoice = () => {
    let amountToInvoice = form.getFieldValue('toInvoice');
    let mode = 'MANUAL';
    if (amountToInvoice === 0) {
      notification.error({ message: 'Please enter an amount' });
      return;
    }

  
    genLateChargeInvoiceBytpid({
      tpid: tpid ?? 0,
      amountToInvoice: amountToInvoice,
      calidList: state.listCalid,
      jobid: 0,
      mode: mode,
    }).then((res) => {
      state.dataCalc = res.list || [];
      state.isLoading = false;
      refetch();
      setState({ ...state, totalSelected:0 });
      form.setFieldsValue({toInvoice:0,totalSelected:0})
      setSelectedRowKeys([]);
     
    
    });
  };
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = {
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: any) => {
     
      setSelectedRowKeys(newSelectedRowKeys)
      let total = 0;
      let listCalid = [];
      for (const i in selectedRows) {
        total += selectedRows[i].calculatedamount;
        listCalid.push(selectedRows[i].calid);
      }
      form.setFieldsValue({ toInvoice: total });

      updateState({ totalSelected: total, listCalid: listCalid });
    },
    getCheckboxProps: (record: CiLateCharge) => ({
      disabled:
        record.flagblockinvoicing === true ||
        record.lctype !== 'CALCUL' ||
        record.ciidlatecharge != null,
      name: record.ctreference,
    }),
    selectedRowKeys:selectedRowKeys
  };

  return (
    <DF.Provider pagcode="LateCharge">
      <Form form={form} name="form">
        <Row>
          <Col span={6}>
            <Form.Item name="LastCalculationDate" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="lastCalcDate">
                <Value value={state.lastCalcDate} />
              </FormInput>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="totalDueAmount" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totalDueAmount">
                <Value
                  value={toCurrency(
                    state.totalDueAmount,
                    formG.getFieldValue('currcode')
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="totalLateCharge" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totalLateCharge">
                <Value
                  value={toCurrency(
                    state.totalLateCharge,
                    formG.getFieldValue('currcode')
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>

          <Col span={6}>
            {!showAsModal && (
              <Button
                loading={state.isLoading}
                comcode="runCalculate"
                type="primary"
                onClick={calculate}
              />
            )}
          </Col>
        </Row>
        {state.dataCalc.length !== 0 ? (
          <Row>
            <Col span={6}>
              <Form.Item name="totalInvoiced" shouldUpdate noStyle>
                <FormInput col xs={24} comcode="totalInvoiced">
                  <Value
                    value={toCurrency(
                      state.totalInvoiced,
                      formG.getFieldValue('currcode')
                    )}
                  />
                </FormInput>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="totalSelected" shouldUpdate noStyle>
                <FormInput col xs={24} comcode="totalSelected">
                  <Value
                    value={toCurrency(
                      state.totalSelected,
                      formG.getFieldValue('currcode')
                    )}
                  />
                </FormInput>
              </Form.Item>
            </Col>

            <Col span={6}>
              <GetLabel label="agreedAmountToInvoice" />
              <Form.Item name="toInvoice" shouldUpdate>
                <InputCurrency
                  col
                  md={12}
                  width="50%"
                  currcode={formG.getFieldValue('currcode')}
                  name='toInvoice'
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  return (
                    !showAsModal && (
                      <Button
                        loading={isFetching}
                        comcode="runInvoice"
                        type="primary"
                        disabled={
                          getFieldValue('toInvoice') === 0 ||
                          isNil(getFieldValue('toInvoice'))
                        }
                        onClick={invoice}
                      />
                    )
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        ) : null}
      </Form>

      <Title comcode="LateCharge" level={5}></Title>
      <DataGrid
        dataSource={data}
        columns={
          !showAsModal
            ? columns
            : columns.filter((col) => col.dataIndex !== 'action')
        }
        isLoading={isFetching}
        rowKey={(record) => record.calid}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
          
        }}
        scroll={{ x: 1000, y: 400 }}
        pagination={{ pageSize: 1000 }}
      ></DataGrid>
    </DF.Provider>
  );
};

export default LateCharge;
